// Question Session Report run detail — the generation of one report for a
// submitted attempt. Reads the DEDICATED `question-session-report-runs` record
// (the OperationRun), then its model runs (keyed by the session id, which the
// report ModelOutputs are saved against). Mirrors ExtractionDetail using the
// shared operation-detail components.
//
// The report has no source question/student solution — the digest is passed as
// one JSON payload — so those sections are dropped; what matters is the run
// metadata (status/tokens/session) and the per-run prompt + raw JSON report.
import { useEffect, useState } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput } from '../../types';
import {
    OperationDetailPage,
    ContextGrid,
    MetadataCard,
    MetaItem,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
    formatCost,
    totalRunCost,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import './SolutionAnalysisDetail.css';

export function QuestionSessionReportDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    const { data: runData, isLoading, error } = useQuery({
        queryKey: ['questionSessionReportRun', id],
        queryFn: () => adminApi.getQuestionSessionReportRun(id!),
        enabled: !!id,
    });
    const run = runData?.run;

    // Report ModelOutputs are saved against the session id (submissionId).
    const sessionId = run?.correlation?.sessionId;
    const { data: runs = [] } = useQuery({
        queryKey: ['modelRuns', sessionId],
        queryFn: () => adminApi.getModelRuns(sessionId!),
        enabled: !!sessionId,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });
    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find((r) => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const goBack = () => navigate('/submissions?tab=questionsessionreport');

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading report run…</div></div>;
    }
    if (error || !run) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading report run: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={goBack}>Back to Question Session Report</button>
            </div>
        );
    }

    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Question Session Report Detail' },
    });
    const selectedRun = runs.find((r) => r.id === selectedRunId);
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRawResponse));

    const parsedBody = selectedRun && parsedHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRawResponse}
        />
    ) : undefined;

    return (
        <OperationDetailPage
            title="Question Session Report Details"
            id={run.id}
            backLabel="Question Session Report"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Report" />}
            onBack={goBack}
            context={
                <ContextGrid
                    left={
                        <MetadataCard title="Report">
                            <MetaItem label="Student">
                                {run.studentName || run.userId}
                                {run.studentEmail && <span className="muted"> · {run.studentEmail}</span>}
                            </MetaItem>
                            <MetaItem label="Session">
                                <span className="datetime" style={{ fontFamily: 'monospace' }}>{sessionId || '—'}</span>
                            </MetaItem>
                            {run.correlation?.questionSetId && (
                                <MetaItem label="Assignment">
                                    <a role="button" style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/question-sets/${run.correlation!.questionSetId}`)}>
                                        {run.correlation.questionSetId} →
                                    </a>
                                </MetaItem>
                            )}
                        </MetadataCard>
                    }
                    right={
                        <MetadataCard title="Run">
                            <MetaItem label="Status"><span className={`tag status-${run.status}`}>{run.status}</span></MetaItem>
                            <MetaItem label="Latency">{formatLatency(run.latencyMs)}</MetaItem>
                            <MetaItem label="Started">
                                <span className="datetime">{format(new Date(run.startedAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                            {run.completedAt && (
                                <MetaItem label="Completed">
                                    <span className="datetime">{format(new Date(run.completedAt), 'MMM dd, HH:mm:ss')}</span>
                                </MetaItem>
                            )}
                            {run.llm && (
                                <MetaItem label="Tokens / Cost">
                                    {run.llm.inputTokens.toLocaleString()} in / {run.llm.outputTokens.toLocaleString()} out
                                    {/* Every call of the operation (the runs below), not the record's own figure. */}
                                    {(totalRunCost(runs) ?? run.llm.costUsd) != null && <span className="muted"> · {formatCost(totalRunCost(runs) ?? run.llm.costUsd!)}</span>}
                                </MetaItem>
                            )}
                            {run.error && (
                                <MetaItem label="Error"><span className="tag status-failed">{run.error}</span></MetaItem>
                            )}
                        </MetadataCard>
                    }
                />
            }
            runs={runs.length > 0 ? (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={(r) => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={(r) => setSelectedRunId(r.id)}
                    getRowClass={(r) => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            ) : (
                <div className="card"><p className="muted">No model runs recorded for this report.</p></div>
            )}
            runDetail={
                selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={runError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedBody}
                    />
                )
            }
        />
    );
}
